import {
  ArrowsRightLeftIcon,
  BanknotesIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  InformationCircleIcon,
  TicketIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";

import { getPlatformWallet } from "../api/walletPaymentsApi";

// ============================================================================
// Helpers
// ============================================================================

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("en-NG", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const formatLabel = (value: string | null | undefined) => (value ? value.replace(/_/g, " ") : "—");

const formatNumber = (value: number | null | undefined) =>
  value == null ? "—" : new Intl.NumberFormat("en-NG").format(value);

// ============================================================================
// UI
// ============================================================================

const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  const s = status ?? "UNKNOWN";
  const styles: Record<string, string> = {
    ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    PROCESSING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    CANCELLED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    FROZEN: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    CLOSED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    REVERSED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
        styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      {formatLabel(s)}
    </span>
  );
};

const DirectionBadge = ({ direction }: { direction: string | null | undefined }) => {
  const isCredit = direction === "CREDIT";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
        isCredit
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
          : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
      }`}
    >
      {formatLabel(direction)}
    </span>
  );
};

const TypeBadge = ({ type }: { type: string | null | undefined }) => {
  const styles: Record<string, string> = {
    COMMISSION: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
    ADMIN_CREDIT: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
    ADMIN_DEBIT: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    REFUND: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
        styles[type ?? ""] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      {formatLabel(type)}
    </span>
  );
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  subtitle,
  tone = "slate",
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  subtitle?: string;
  tone?: "emerald" | "violet" | "blue" | "amber" | "rose" | "cyan" | "slate";
}) => {
  const tones: Record<string, { bg: string; icon: string; value: string }> = {
    emerald: {
      bg: "bg-emerald-50 dark:bg-emerald-900/20",
      icon: "text-emerald-500",
      value: "text-emerald-700 dark:text-emerald-300",
    },
    violet: {
      bg: "bg-violet-50 dark:bg-violet-900/20",
      icon: "text-violet-500",
      value: "text-violet-700 dark:text-violet-300",
    },
    blue: {
      bg: "bg-blue-50 dark:bg-blue-900/20",
      icon: "text-blue-500",
      value: "text-blue-700 dark:text-blue-300",
    },
    amber: {
      bg: "bg-amber-50 dark:bg-amber-900/20",
      icon: "text-amber-500",
      value: "text-amber-700 dark:text-amber-300",
    },
    rose: {
      bg: "bg-rose-50 dark:bg-rose-900/20",
      icon: "text-rose-500",
      value: "text-rose-700 dark:text-rose-300",
    },
    cyan: {
      bg: "bg-cyan-50 dark:bg-cyan-900/20",
      icon: "text-cyan-500",
      value: "text-cyan-700 dark:text-cyan-300",
    },
    slate: {
      bg: "bg-slate-50 dark:bg-slate-800/80",
      icon: "text-slate-500",
      value: "text-slate-900 dark:text-white",
    },
  };

  const c = tones[tone] || tones.slate;

  return (
    <div className={`rounded-2xl ${c.bg} p-5`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-5 w-5 ${c.icon}`} />
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <p className={`mt-2 text-2xl font-bold ${c.value}`}>{value}</p>
      {subtitle ? (
        <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
      ) : null}
    </div>
  );
};

const MetaRow = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
    <span
      className={`text-right text-sm font-medium text-slate-900 dark:text-white ${
        mono ? "font-mono text-xs" : ""
      }`}
    >
      {value || "—"}
    </span>
  </div>
);

const SectionCard = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-primary-500" />
      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
    </div>
    {children}
  </div>
);

// ============================================================================
// Component
// ============================================================================

export const PlatformWalletRoute = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["platform-wallet"],
    queryFn: getPlatformWallet,
  });

  const wallet = data?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
      </div>
    );
  }

  if (error) {
    return <div className="py-20 text-center text-sm text-rose-500">{String(error)}</div>;
  }

  if (!wallet) {
    return (
      <div className="py-20 text-center">
        <WalletIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Platform wallet data not available.
        </p>
      </div>
    );
  }

  const summary = wallet.commissionSummary;
  const typeBreakdown = wallet.transactionTypeBreakdown ?? [];
  const recentTransactions = wallet.recentTransactions ?? [];

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-violet-50 via-white to-emerald-50 p-8 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-xl shadow-violet-500/25">
              <BanknotesIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Platform Wallet Balance
              </p>
              <p className="text-4xl font-bold text-slate-900 dark:text-white">
                {formatCurrency(wallet.balance, wallet.currency)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={wallet.status} />
                <span className="inline-flex rounded-full bg-white/80 px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300">
                  {wallet.currency}
                </span>
                <span className="inline-flex rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                  {wallet.commissionPercentage}% commission
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px]">
            <div className="rounded-2xl bg-white/75 p-4 shadow-sm dark:bg-slate-900/80">
              <p className="text-xs text-slate-400">Last Transaction</p>
              <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                {formatDate(wallet.lastTransactionAt)}
              </p>
            </div>
            <div className="rounded-2xl bg-white/75 p-4 shadow-sm dark:bg-slate-900/80">
              <p className="text-xs text-slate-400">Wallet Created</p>
              <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                {formatDate(wallet.createdAt)}
              </p>
            </div>
            <div className="rounded-2xl bg-white/75 p-4 shadow-sm dark:bg-slate-900/80">
              <p className="text-xs text-slate-400">Wallet ID</p>
              <p className="mt-1 font-mono text-xs text-slate-700 dark:text-slate-200">
                {wallet.walletId}
              </p>
            </div>
            <div className="rounded-2xl bg-white/75 p-4 shadow-sm dark:bg-slate-900/80">
              <p className="text-xs text-slate-400">Platform User ID</p>
              <p className="mt-1 font-mono text-xs text-slate-700 dark:text-slate-200">
                {wallet.platformUserId}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Commission Health</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Quick monitoring metrics for platform earnings and commission activity.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={CurrencyDollarIcon}
            label="Total Earned"
            value={formatCurrency(summary.totalEarned, wallet.currency)}
            subtitle="Lifetime commission"
            tone="emerald"
          />
          <StatCard
            icon={CalendarIcon}
            label="Today"
            value={formatCurrency(summary.today, wallet.currency)}
            subtitle="Commission booked today"
            tone="blue"
          />
          <StatCard
            icon={ChartBarIcon}
            label="Last 7 Days"
            value={formatCurrency(summary.last7Days, wallet.currency)}
            subtitle={`${formatNumber(summary.transactionsLast7Days)} transactions`}
            tone="violet"
          />
          <StatCard
            icon={ChartBarIcon}
            label="Last 30 Days"
            value={formatCurrency(summary.last30Days, wallet.currency)}
            subtitle={`${formatNumber(summary.transactionsLast30Days)} transactions`}
            tone="cyan"
          />
          <StatCard
            icon={ArrowsRightLeftIcon}
            label="All Commission Transactions"
            value={formatNumber(summary.totalTransactions)}
            subtitle="Total commission entries"
            tone="amber"
          />
          <StatCard
            icon={CurrencyDollarIcon}
            label="Average per Transaction"
            value={formatCurrency(summary.averagePerTransaction, wallet.currency)}
            subtitle="Average commission amount"
            tone="rose"
          />
          <StatCard
            icon={TicketIcon}
            label="Rides With Commission"
            value={formatNumber(summary.totalRidesWithCommission)}
            subtitle="Trips that generated revenue"
            tone="slate"
          />
          <StatCard
            icon={InformationCircleIcon}
            label="Configured Commission"
            value={`${wallet.commissionPercentage}%`}
            subtitle="Current platform commission rate"
            tone="slate"
          />
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <SectionCard title="Where The Money Came From" icon={ArrowsRightLeftIcon}>
          {typeBreakdown.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No transaction breakdown available.
            </p>
          ) : (
            <div className="space-y-3">
              {typeBreakdown.map((item) => (
                <div
                  key={`${item.type}-${item.count}`}
                  className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <TypeBadge type={item.type} />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {formatNumber(item.count)} entries
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      Total amount by transaction type
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {formatCurrency(item.totalAmount, wallet.currency)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Current Wallet State" icon={WalletIcon}>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <MetaRow
              label="Current Balance"
              value={formatCurrency(wallet.balance, wallet.currency)}
            />
            <MetaRow label="Currency" value={wallet.currency} />
            <MetaRow label="Wallet Status" value={<StatusBadge status={wallet.status} />} />
            <MetaRow label="Last Transaction At" value={formatDate(wallet.lastTransactionAt)} />
            <MetaRow label="Created At" value={formatDate(wallet.createdAt)} />
            <MetaRow label="Commission Percentage" value={`${wallet.commissionPercentage}%`} />
            <MetaRow label="Wallet ID" value={wallet.walletId} mono />
            <MetaRow label="Platform User ID" value={wallet.platformUserId} mono />
          </div>
        </SectionCard>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          Recent Platform Transactions
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Recent ledger entries with booking and payment context for commission investigations.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {formatNumber(recentTransactions.length)} recent entries
          </span>
          <span className="inline-flex rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
            Expand a row to inspect booking and payment context
          </span>
        </div>

        <div className="mt-4 space-y-5">
          {recentTransactions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No recent platform transactions yet.
              </p>
            </div>
          ) : (
            recentTransactions.map((transaction) => (
              <details
                key={transaction.transactionId}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition dark:border-slate-800 dark:bg-slate-900"
              >
                <summary className="cursor-pointer list-none p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <TypeBadge type={transaction.type} />
                        <DirectionBadge direction={transaction.direction} />
                        <StatusBadge status={transaction.status} />
                      </div>
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </p>
                          <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
                            {transaction.description || "No description"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 self-start rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 transition group-open:bg-primary-50 group-open:text-primary-700 dark:bg-slate-800 dark:text-slate-300 dark:group-open:bg-primary-900/20 dark:group-open:text-primary-300">
                          <span>Details</span>
                          <ChevronDownIcon className="h-4 w-4 transition-transform group-open:rotate-180" />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[520px]">
                      <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/70">
                        <p className="text-xs text-slate-400">Processed At</p>
                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                          {formatDate(transaction.processedAt)}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/70">
                        <p className="text-xs text-slate-400">Created At</p>
                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                          {formatDate(transaction.createdAt)}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/70">
                        <p className="text-xs text-slate-400">Reference</p>
                        <p className="mt-1 truncate font-mono text-xs text-slate-700 dark:text-slate-200">
                          {transaction.reference}
                        </p>
                      </div>
                    </div>
                  </div>
                </summary>

                <div className="border-t border-slate-100 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <IdentificationIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">Transaction ID</span>
                      </div>
                      <p className="mt-2 font-mono text-xs text-slate-700 dark:text-slate-200">
                        {transaction.transactionId}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <ClockIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">Direction</span>
                      </div>
                      <div className="mt-2">
                        <DirectionBadge direction={transaction.direction} />
                      </div>
                    </div>
                    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">Status</span>
                      </div>
                      <div className="mt-2">
                        <StatusBadge status={transaction.status} />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <SectionCard title="Related Booking" icon={TicketIcon}>
                      {transaction.relatedBooking ? (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                          <MetaRow
                            label="Booking ID"
                            value={transaction.relatedBooking.bookingId}
                            mono
                          />
                          <MetaRow
                            label="Ride Offer ID"
                            value={transaction.relatedBooking.rideOfferId}
                            mono
                          />
                          <MetaRow
                            label="Rider ID"
                            value={transaction.relatedBooking.riderId}
                            mono
                          />
                          <MetaRow
                            label="Number of Seats"
                            value={formatNumber(transaction.relatedBooking.numberOfSeats)}
                          />
                          <MetaRow
                            label="Total Booking Price"
                            value={formatCurrency(
                              transaction.relatedBooking.totalPrice,
                              transaction.relatedBooking.currency
                            )}
                          />
                          <MetaRow
                            label="Platform Share"
                            value={formatCurrency(
                              transaction.relatedBooking.platformShare,
                              transaction.relatedBooking.currency
                            )}
                          />
                          <MetaRow
                            label="Driver Share"
                            value={formatCurrency(
                              transaction.relatedBooking.driverShare,
                              transaction.relatedBooking.currency
                            )}
                          />
                          <MetaRow
                            label="Payment Reference"
                            value={transaction.relatedBooking.bookingPaymentReference ?? "—"}
                            mono
                          />
                          <MetaRow
                            label="Payment Method"
                            value={formatLabel(transaction.relatedBooking.bookingPaymentMethod)}
                          />
                          <MetaRow
                            label="Payment Status"
                            value={
                              transaction.relatedBooking.bookingPaymentStatus ? (
                                <StatusBadge
                                  status={transaction.relatedBooking.bookingPaymentStatus}
                                />
                              ) : (
                                "—"
                              )
                            }
                          />
                          <MetaRow
                            label="Driver Share Status"
                            value={formatLabel(transaction.relatedBooking.driverShareStatus)}
                          />
                          <MetaRow
                            label="Booking Status"
                            value={
                              <StatusBadge status={transaction.relatedBooking.bookingStatus} />
                            }
                          />
                          <MetaRow
                            label="Confirmed At"
                            value={formatDate(transaction.relatedBooking.confirmedAt)}
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          No linked booking on this transaction.
                        </p>
                      )}
                    </SectionCard>

                    <SectionCard title="Related Payment" icon={CreditCardIcon}>
                      {transaction.relatedPayment ? (
                        <div className="space-y-4">
                          {transaction.relatedPayment.failureReason ? (
                            <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900 dark:bg-rose-900/20">
                              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-rose-500" />
                              <div>
                                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                                  Failure Reason
                                </p>
                                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
                                  {transaction.relatedPayment.failureReason}
                                </p>
                              </div>
                            </div>
                          ) : null}

                          <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <MetaRow
                              label="Payment ID"
                              value={transaction.relatedPayment.paymentId}
                              mono
                            />
                            <MetaRow
                              label="User ID"
                              value={transaction.relatedPayment.userId}
                              mono
                            />
                            <MetaRow
                              label="Amount"
                              value={formatCurrency(
                                transaction.relatedPayment.amount,
                                transaction.relatedPayment.currency
                              )}
                            />
                            <MetaRow
                              label="Purpose"
                              value={formatLabel(transaction.relatedPayment.purpose)}
                            />
                            <MetaRow
                              label="Reference"
                              value={transaction.relatedPayment.reference}
                              mono
                            />
                            <MetaRow
                              label="External Reference"
                              value={transaction.relatedPayment.externalReference ?? "—"}
                              mono
                            />
                            <MetaRow
                              label="Payment Status"
                              value={<StatusBadge status={transaction.relatedPayment.status} />}
                            />
                            <MetaRow
                              label="Method"
                              value={formatLabel(transaction.relatedPayment.method)}
                            />
                            <MetaRow
                              label="Payment Channel"
                              value={transaction.relatedPayment.paymentChannel ?? "—"}
                            />
                            <MetaRow
                              label="Authorization Code"
                              value={transaction.relatedPayment.authorizationCode ?? "—"}
                              mono
                            />
                            <MetaRow
                              label="Paid At"
                              value={formatDate(transaction.relatedPayment.paidAt)}
                            />
                            <MetaRow
                              label="Webhook Received"
                              value={formatDate(transaction.relatedPayment.webhookReceivedAt)}
                            />
                            <MetaRow
                              label="Webhook Retry Count"
                              value={formatNumber(transaction.relatedPayment.webhookRetryCount)}
                            />
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          No linked payment on this transaction.
                        </p>
                      )}
                    </SectionCard>
                  </div>
                </div>
              </details>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
